import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {
  TileElemOptions,
  TileElemSetup,
  TileMultiChoiceElemSetup,
  TileNumberElemSetup,
  TileToggleElemSetup,
} from '../../../shared/models/tile-template/interfaces';
import {
  Tile,
  TILE_TYPE_SIMPLE_ANALOG,
  TILE_TYPE_SIMPLE_BOOL,
  TILE_TYPE_SIMPLE_MULTISTATE,
  TILE_TYPE_SUPER_TREE,
  TILE_TYPE_SUPER_CANVAS,
  TileId,
} from '../../../shared/models/tile';
import {SimpleAnalogStructure, SimpleBoolStructure, SimpleMultistateStructure} from '../../../shared/models/structure';
import {Subscription} from 'rxjs';
import {AppState} from '../../../app/app.state';
import {Store} from '@ngrx/store';
import {filter, map} from 'rxjs/operators';
import {SessionService} from '../../../core/services/session.service';
import {TilesService} from '../../../core/services/tiles.service';
import {TileEditorComponent} from '../tile-editor/tile-editor.component';
import {TileToggleComponent} from './types/toggle/tile-toggle.component';
import {TileMultiChoiceComponent} from './types/multi-choice/tile-multi-choice.component';
import {TileNumberComponent} from './types/number/tile-number.component';
import { EMPTY_ID } from 'src/shared/constants/empty-id';
import {ColorOption} from '@shared/models/appearance';
import {isValidTileColor} from '@shared/utils/color.util';

@Component({
  selector: 'motn-tile',
  templateUrl: './tile.component.html',
  styleUrls: ['./tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TileEditorComponent,
    TileToggleComponent,
    TileMultiChoiceComponent,
    TileNumberComponent,
  ],
})
export class TileComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Supply elemSetup to override defaults for how any given tile
   * would be set up by default. If not supplied, rawTile must be
   * supplied and an elemSetup will be auto-generated using defaults
   * from that rawTile object.
   *
   * The purpose of offering this ability is for curated apps,
   * where we don't want the tile configuration itself to dictate
   * how a tile appears/works. For example, a set point in a tenant
   * apartment--when the tile is rendered in the tenant's app, we
   * have a very specific setup we follow, but if the same tile
   * is also rendered in a tile dashboard, we're fine with the
   * tile following the settings in its config. In the former
   * situation, we'd supply a carefully tailored elemSetup, but in
   * the latter, we'd be fine just supplying rawTile and letting
   * the defaults apply.
   */
  @Input() elemSetup: TileElemSetup;
  @Input() rawTile: Tile;
  @Input() hideAlarmMenu: boolean = false;

  editingTile: boolean = false;

  tileId: TileId;
  private tileSub: Subscription;


  constructor(
    private tilesService: TilesService,
    private session: SessionService,
    private store: Store<AppState>,
  ) {
  }


  ngOnInit(): void {
    if (this.elemSetup && this.rawTile) {
      throw new Error('Must only supply either @Input to TileMinimalComponent, not both');
    }

    if (!this.elemSetup) {
      if (!this.rawTile) {
        throw new Error('Neither @Input was supplied to TileMinimalComponent');
      }

      this.setUpTileElem();
    }

    try {
      // Try to get ID from elemSetup first, then fallback to rawTile (for canvas tiles)
      this.tileId = this.elemSetup?.tile?.ID || this.rawTile?.ID || EMPTY_ID;
    } catch (e) {
      this.tileId = EMPTY_ID;
      console.error('No tile ID can be ascertained in TileMinimalComponent');
    }

    this.tileSub = this.store.select('tiles').pipe(
      map(
        (state) => {
          return state.all[this.tileId];
        },
      ),
      filter(t => !!t),
    ).subscribe(
      (tile) => {
        this.rawTile = Tile.create(tile);
        this.setUpTileElem();
      },
    );
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rawTile && !changes.rawTile.firstChange && this.rawTile) {
      this.setUpTileElem();
    }
  }


  ngOnDestroy() {
    this.tileSub?.unsubscribe();
  }


  private setUpTileElem() {
    const tile = this.rawTile;
    const elemOptions: TileElemOptions = {
      tile,
      displayColor: isValidTileColor(tile.Appearance.Color),
      displayIcon: tile.Appearance.Icon,
      displayName: tile.Appearance.DisplayName,
      hideAlarmMenu: this.hideAlarmMenu,
    };

    switch (tile.Type) {
      case TILE_TYPE_SIMPLE_BOOL:
        this.elemSetup = new TileToggleElemSetup({
          ...elemOptions,
          showStateText: true,
          showToggle: true,
          writable: (tile.Structure as SimpleBoolStructure).Writable,
        });

        break;
      case TILE_TYPE_SIMPLE_ANALOG:
        this.elemSetup = new TileNumberElemSetup({
          ...elemOptions,
          writable: (tile.Structure as SimpleAnalogStructure).Writable,
        });

        break;
      case TILE_TYPE_SIMPLE_MULTISTATE:
        this.elemSetup = new TileMultiChoiceElemSetup({
          ...elemOptions,
          writable: (tile.Structure as SimpleMultistateStructure).Writable,
        });

        break;
      case TILE_TYPE_SUPER_TREE:
        break;
      default:
        throw new Error('Unsupported type of tile supplied to TileMinimalComponent');
    }
  }


  openTileEditor() {
    this.tilesService.canConfigureTile$(this.tileId).subscribe({
      next: (canConfigure) => {
        this.editingTile = canConfigure;
      },
      error: () => {
        this.editingTile = false;
      }
    });
  }


  setColorAndIcon(): void {

  }

}
